/// <amd-module name="Release/Landing/HubContent/Releases/EnvironmentsColumn" />
import * as React from "react";

import { VssComponent, IVssComponentProperties, ReactRootComponent, IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageContext } from "VSS/Platform/Context";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { Release, ReleaseStatus, ReleaseEnvironment } from "Release/Client/Contracts/Release";
import { EnvironmentsChain } from "Release/EnvironmentNode/EnvironmentChain";
import { ReleasesViewCanvasConstants, RmServiceInstanceId } from "Release/Landing/Constants";
import { ReleaseManagementNamespace, userHasMemberClaim, getFullSecurityToken, hasRmEnvironmentPermissions, ReleaseManagementSecurityPermissions } from "Release/Helpers/Permission";
import { isSignalRConnected } from "Release/Landing/SignalR/LandingSignalRHub";
import { ActiveReleasesActionCreator } from "./ActiveReleasesActionCreator";

export interface IActiveReleaseColumnProps extends IVssComponentProperties {
    release: Release;
    definitionId: number;
    instanceId: string;
    definitionEnvironmentCurrentReleaseMap: { [key: number]: number };
    envNodeWidth: number;
    visibleEnvCount: number;
    onExpandClick: (widthOnExpand: number) => void;
    onDesiredReleaseFound?: () => void;
}

export class EnvironmentsColumn extends VssComponent<IActiveReleaseColumnProps, {}> {
    public render(): React.ReactNode {
        let release = this.props.release as Release;
        const showApprovalPanel = userHasMemberClaim(this.context.pageContext);

        return < EnvironmentsChain
            definitionId={this.props.definitionId}
            environments={release.environments}
            cssClass="active-rel-env-cell"
            definitionEnvironmentCurrentReleaseMap={this.props.definitionEnvironmentCurrentReleaseMap}
            envNodeWidth={this.props.envNodeWidth}
            envNodeMinWidth={ReleasesViewCanvasConstants.EnvironmentNodeWidthSmall}
            envNodeRightMargin={ReleasesViewCanvasConstants.EnvironmentNodeRightMargin}
            visibleEnvCount={this.props.visibleEnvCount}
            releaseId={release.id}
            onExpandClick={this.props.onExpandClick}
            onReleaseFound={this.props.onDesiredReleaseFound}
            preventClickOnNode={this.props.release.status === ReleaseStatus.Draft}
            onNodeWithApprovalClicked={showApprovalPanel ? (envId: number) => renderApproveRejectPanel(this.context.pageContext, this.props.release, envId) : undefined}
            onNodeWithMIClicked={showApprovalPanel ? (envId: number) => renderMIPanel(this.context.pageContext, this.props.release, envId) : undefined}
        />;
    }
}

function renderApproveRejectPanel(pageContext: IVssPageContext, release: Release, environmentId: number): void {
    renderInPagePanel(
        pageContext,
        release,
        environmentId,
        "releases-hub-approval-panel",
        {
            onApproveRejectReassign: (releaseEnvironment: ReleaseEnvironment) => {
                if (!isSignalRConnected()) {
                    // Initially after the approval, the environment is not immediately updated
                    // Wait for a second and fetch again

                    setTimeout(() =>
                        ActionCreatorManager.GetActionCreator<ActiveReleasesActionCreator>(ActiveReleasesActionCreator).fetchAndUpdateEnvironment(
                            pageContext,
                            release.releaseDefinition.id,
                            release.id,
                            environmentId,
                            false
                        ), 1000);
                }
            }
        },
        true);
}

function renderMIPanel(pageContext: IVssPageContext, release: Release, environmentId: number): void {
    const releaseEnvironment = release.environments.find(env => env.id === environmentId);
    if (releaseEnvironment) {
        getReleaseEnvironmentPermissionsIfNeeded(pageContext, release, environmentId).
            then(() => {

                const hasManageDeploy = hasRmEnvironmentPermissions(pageContext, release.releaseDefinition.path, release.releaseDefinition.id, releaseEnvironment.definitionEnvironmentId, ReleaseManagementSecurityPermissions.ManageDeployments);

                renderInPagePanel(
                    pageContext,
                    release,
                    environmentId,
                    "releases-hub-mi-panel",
                    {
                        onResumeRejectClick: (releaseEnvironment: ReleaseEnvironment) => {
                            if (!isSignalRConnected()) {
                                // Initially after the approval, the environment is not immediately updated
                                // Wait for a second and fetch again

                                setTimeout(() =>
                                    ActionCreatorManager.GetActionCreator<ActiveReleasesActionCreator>(ActiveReleasesActionCreator).fetchAndUpdateEnvironment(
                                        pageContext,
                                        release.releaseDefinition.id,
                                        release.id,
                                        environmentId,
                                        false
                                    ), 1000);
                            }
                        },
                        hasManageDeployPermissions: hasManageDeploy
                    },
                    false);
            });
    }
}

function renderInPagePanel(pageContext: IVssPageContext, release: Release, environmentId: number, componentId: string, extraProps: any, waitForPermissions: boolean): void {
    const releaseEnvironment = release.environments.find(env => env.id === environmentId);
    if (releaseEnvironment) {
        const renderPanel = () => {
            pageContext.getService<IVssLayoutManager>("IVssLayoutManager").renderCallout((onDismiss: () => void) => {
                return (
                    <ReactRootComponent pageContext={pageContext}>
                        <LegacyComponent
                            modules={["PipelineWorkflow/Scripts/Shared/LegacyLWP/ReleasesHubApprovalPanel"]}
                            dependentContributionIds={["ms.vss-releaseManagement-web.release-service-data"]}
                            wrappedType={componentId}
                            dependencies={[
                                "ms.vss-web.legacy-vss-ui-content"
                            ]}
                            loadingComponent={() => <Spinner className="flex flex-grow absolute-fill" size={SpinnerSize.large} />}
                            onDismiss={onDismiss}
                            releaseEnvironmentId={environmentId}
                            releaseId={release.id}
                            {...extraProps}
                        />
                    </ReactRootComponent >);
            });
        }

        if (waitForPermissions) {
            getReleaseEnvironmentPermissionsIfNeeded(pageContext, release, environmentId)
                .then(renderPanel);
        }
        else {
            renderPanel();
        }
    }
}

function getReleaseEnvironmentPermissionsIfNeeded(pageContext: IVssPageContext, release: Release, environmentId: number): Promise<void> {
    const releaseEnvironment = release.environments.find(env => env.id === environmentId);
    if (releaseEnvironment) {
        const environmentToken = getFullSecurityToken(pageContext, release.releaseDefinition.path, release.releaseDefinition.id, releaseEnvironment.definitionEnvironmentId);
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const permissions = contributionService.getSharedData<{ [key: string]: any }>("_permissions");
        const rmNamespaceLowerCase = ReleaseManagementNamespace.toLowerCase();
        if (!permissions || !permissions[rmNamespaceLowerCase] || !permissions[rmNamespaceLowerCase][environmentToken]) {
            return contributionService.getDataAsync(
                "ms.vss-releaseManagement-web.rm-permissions-data-provider",
                {
                    folderPath: release.releaseDefinition.path,
                    definitionId: release.releaseDefinition.id,
                    definitionEnvironmentId: releaseEnvironment.definitionEnvironmentId
                },
                true,
                RmServiceInstanceId);
        }
    }

    return Promise.resolve(void (0));
}

"releases-hub-mi-panel"